import { useCallback, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { motion, AnimatePresence } from 'framer-motion';
import { mlEvents, trainingLossData, validationLossData } from '@/data/mlEvents';

interface LossGraphProps {
  onEventClick?: (event: string) => void;
}

export function LossGraph({ onEventClick }: LossGraphProps) {
  const [clickedEvent, setClickedEvent] = useState<string | null>(null);
  const [glitchActive, setGlitchActive] = useState(false);

  const handleDotClick = useCallback(
    (data: { month: string; loss: number }, event: string) => {
      if (!event) return;

      setClickedEvent(event);

      if (event.includes('Chaos') || event.includes('Дедлайны')) {
        setGlitchActive(true);
        setTimeout(() => setGlitchActive(false), 1000);
      }

      onEventClick?.(event);
    },
    [onEventClick]
  );

  const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ dataKey: string; value: number; name: string; payload?: { month: string } }> }) => {
    if (!active || !payload?.length) return null;

    const month = payload[0].payload?.month;
    const eventData = mlEvents.find((e) => e.month === month);

    if (!eventData) return null;

    return (
      <div className="p-3 bg-[#202020] border border-[#2F2F2F] rounded-sm shadow-lg">
        {eventData.event && (
          <div className="font-semibold text-white/90 flex items-center gap-2">
            <span>{eventData.emoji}</span>
            <span>{eventData.event}</span>
          </div>
        )}
        {eventData.description && (
          <p className="text-[#9B9A97] text-xs mt-2 max-w-xs">{eventData.description}</p>
        )}
        <div className="text-[#9B9A97] text-xs mt-2">
          <div>Training Loss: {payload[0].value?.toFixed(2)}</div>
          {payload[1] && (
            <div>Validation Loss: {payload[1].value?.toFixed(2)}</div>
          )}
        </div>
      </div>
    );
  };

  const CustomDot = ({ cx, cy, payload }: { cx?: number; cy?: number; payload?: { month: string; loss: number } }) => {
    const event = mlEvents.find((e) => e.month === payload?.month && e.event);
    if (!event || !cx || !cy) return null;

    return (
      <g>
        <motion.circle
          cx={cx}
          cy={cy}
          r={6}
          fill={event.type === 'spike' ? '#FF6B68' : '#6A8759'}
          className="cursor-pointer"
          onClick={() => handleDotClick(payload!, event.event)}
          whileHover={{ scale: 1.5 }}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
        />
      </g>
    );
  };

  return (
    <div className={`relative ${glitchActive ? 'animate-pulse' : ''}`}>
      {glitchActive && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.3 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-red-500/30 pointer-events-none"
        />
      )}

      <div className="p-6 bg-[#191919] rounded-sm border border-[#2F2F2F]">
        <h3 className="text-xl font-semibold text-white/90 mb-4">Градиентный Спуск Года</h3>
        <div className="space-y-2 mb-4 text-sm text-[#9B9A97]">
          <div>Epoch: 21/22</div>
          <div>Learning Rate (η): 0.001</div>
          <div>Optimizer: Adam</div>
          <div>Regularization: Dropout (Sauna/Gym)</div>
        </div>

        <ResponsiveContainer width="100%" height={300} className="sm:h-[400px]">
          <LineChart data={trainingLossData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2F2F2F" />
            <XAxis
              dataKey="month"
              stroke="#9B9A97"
              tick={{ fill: '#9B9A97' }}
              style={{ fontFamily: 'var(--font-mono)' }}
            />
            <YAxis
              stroke="#9B9A97"
              tick={{ fill: '#9B9A97' }}
              domain={[0, 1]}
              style={{ fontFamily: 'var(--font-mono)' }}
            />
            <Tooltip
              content={<CustomTooltip />}
              contentStyle={{
                backgroundColor: '#202020',
                border: '1px solid #2F2F2F',
                color: '#9B9A97',
              }}
              labelStyle={{ color: '#FFFFFF' }}
            />
            <Line
              type="monotone"
              dataKey="loss"
              stroke="#6A8759"
              strokeWidth={2}
              name="Training Loss"
              dot={<CustomDot />}
            />
            <Line
              type="monotone"
              data={validationLossData}
              dataKey="loss"
              stroke="#7193D8"
              strokeWidth={2}
              name="Validation Loss"
              dot={false}
            />
            {mlEvents
              .filter((e) => e.event)
              .map((event, idx) => {
                const dataPoint = trainingLossData.find((d) => d.month === event.month);
                if (!dataPoint) return null;
                return (
                  <ReferenceLine
                    key={idx}
                    x={event.month}
                    stroke="#9B9A97"
                    strokeDasharray="2 2"
                    strokeOpacity={0.5}
                  />
                );
              })}
          </LineChart>
        </ResponsiveContainer>

        <div className="mt-4 flex gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-0.5 bg-[#6A8759]"></div>
            <span className="text-[#9B9A97]">Training Loss</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-0.5 bg-[#7193D8]"></div>
            <span className="text-[#9B9A97]">Validation Loss</span>
          </div>
        </div>

        <AnimatePresence>
          {clickedEvent && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mt-4 p-4 bg-[#202020] border border-[#2F2F2F] rounded-sm"
            >
              {(() => {
                const event = mlEvents.find((e) => e.event === clickedEvent);
                return (
                  <>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">{event?.emoji}</span>
                      <p className="text-white/90 font-medium">{clickedEvent}</p>
                    </div>
                    {event?.description && (
                      <p className="text-[#9B9A97] text-sm">{event.description}</p>
                    )}
                  </>
                );
              })()}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
